import React, { useState, useEffect, useRef, useCallback } from 'react';
import { T, API_BASE } from '../constants';

// TierAlertPanel — in-dashboard surface for the cross-tier 1-minute ADVISORY
// alerts (strategies/api/tiers/monitor.py). These are faster heads-ups on the
// open position (wicked-to-level, armed, giveback risk, TP/stop imminent). They
// are advisory only — the certified 3-minute exit walk still books every fill.
//
// Polls /strategies/api/strategies/tiers/monitor_alerts/?system_id=<sid>.

const ALERT_META = {
  fav_touch:     { label: 'FAV TOUCH',     color: '#10b981' },
  armed:         { label: 'ARMED',         color: '#0ea5e9' },
  giveback_risk: { label: 'GIVEBACK RISK', color: '#f59e0b' },
  tp_imminent:   { label: 'TP IMMINENT',   color: '#10b981' },
  stop_risk:     { label: 'STOP RISK',     color: '#f59e0b' },
  stop_imminent: { label: 'STOP IMMINENT', color: '#ef4444' },
};

function fmtET(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      timeZone: 'America/New_York', hour12: false,
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
}

export default function TierAlertPanel({ systemId, accent = '#0ea5e9', refreshMs = 20000 }) {
  const [alerts, setAlerts] = useState([]);
  const [error, setError]   = useState(null);
  const timerRef = useRef(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const url = `${API_BASE}/strategies/api/strategies/tiers/monitor_alerts/`
                + `?system_id=${systemId}&limit=20`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      if (!j.ok) throw new Error(j.error || 'unknown');
      setAlerts(j.alerts || []);
      setError(null);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [systemId]);

  useEffect(() => {
    fetchAlerts();
    timerRef.current = setInterval(fetchAlerts, refreshMs);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchAlerts, refreshMs]);

  return (
    <div style={{ marginBottom: 14, border: `1px solid ${T.border}`, borderRadius: 6,
                  background: 'rgba(255,255,255,0.01)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                    borderBottom: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.03)' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: accent }} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                       textTransform: 'uppercase', color: T.text }}>
          1-min advisory alerts
        </span>
        <span style={{ fontSize: 10, color: T.textMuted }}>
          faster heads-up · does not change fills
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: T.textMuted }}>
          {alerts.length} recent
        </span>
      </div>

      {error && (
        <div style={{ padding: '8px 12px', fontSize: 11, color: T.bear }}>
          alerts unavailable: {error}
        </div>
      )}

      {!error && alerts.length === 0 && (
        <div style={{ padding: '10px 12px', fontSize: 11, color: T.textMuted,
                      fontStyle: 'italic' }}>
          No advisory alerts yet. They appear while a position is open and price
          approaches arm / TP / stop levels (evaluated on 1-min bars).
        </div>
      )}

      {!error && alerts.length > 0 && (
        <div>
          {alerts.map(a => {
            const m = ALERT_META[a.alert_type] || { label: a.alert_type, color: T.textMuted };
            return (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                                       padding: '6px 12px', borderTop: `1px solid ${T.border}`,
                                       fontSize: 11, fontFamily: 'Roboto Mono, monospace' }}>
                <span style={{ flex: '0 0 110px', display: 'inline-block', padding: '2px 6px',
                               borderRadius: 4, fontSize: 9, fontWeight: 700,
                               letterSpacing: '0.05em', textAlign: 'center',
                               background: `${m.color}22`, border: `1px solid ${m.color}66`,
                               color: m.color }}>
                  {m.label}
                </span>
                <span style={{ flex: '0 0 70px', textAlign: 'right',
                               color: a.bp >= 0 ? T.bull : T.bear }}>
                  {a.bp != null ? `${a.bp >= 0 ? '+' : ''}${a.bp.toFixed(0)}bp` : '—'}
                </span>
                <span style={{ flex: 1, color: T.textMuted, overflow: 'hidden',
                               textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={a.reason || ''}>
                  {a.reason || '—'}
                </span>
                <span style={{ flex: '0 0 64px', textAlign: 'right', color: T.textMuted,
                               fontSize: 10 }}>
                  {fmtET(a.bar_ts || a.created_at)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
